/**
 * El cierre de la inspección (`POST inspection/declare/finalize`): cuándo se reintenta solo y
 * cuánto se espera la respuesta.
 *
 * Contrato acordado con el Gateway para esta ruta, la única que va por su camino estricto:
 *   - 2xx con cuerpo JSON o vacío: cerrada. No se compara el cuerpo contra el texto de éxito, porque
 *     un cambio de redacción en el backend trabaría todos los cierres. Un 2xx con cuerpo que no sea
 *     JSON llegaría como error de parseo y se mostraría como fallido; ninguno de los dos Gateways lo
 *     produce hoy (los dos responden con `res.json`);
 *   - 502: el API respondió con error. Si la base rechaza el cierre, el mismo request vuelve a
 *     fallar igual: reintentarlo solo demora el aviso;
 *   - 504, o un timeout del propio cliente: el resultado es desconocido. El cierre pudo haberse
 *     completado, y reintentarlo lo cerraría dos veces;
 *   - 503: el Gateway lo reserva para los errores previos a la conexión con el API. Solo por eso es
 *     seguro reintentarlo.
 * Un status 0 también queda sin reintento automático: no dice si el request alcanzó a salir.
 */

export const FINALIZE_ENDPOINT = 'inspection/declare/finalize';

/**
 * Tiene que superar el timeout del Gateway hacia el API (`FINALIZE_TIMEOUT_MS`), o el cliente da por
 * fallido un cierre que el Gateway todavía está esperando. El margen es para la red del celular y el
 * tramo hasta el Gateway. `verify:finalize-close` lee los dos valores y falla si no alcanza.
 */
export const FINALIZE_CLIENT_TIMEOUT_MS = 120_000;

/** Lo que importa de un intento fallido. `status` es `null` cuando no hubo respuesta HTTP. */
export interface RequestFailure {
  status: number | null;
  timedOut: boolean;
}

export function shouldRetryFinalize(failure: RequestFailure): boolean {
  return !failure.timedOut && failure.status === 503;
}

/**
 * No hay un texto del backend para este caso. Dice «confirmar» y no «finalizar», porque tras un
 * timeout el cierre pudo haberse completado.
 */
export const CLOSE_FAILED_TEXT = {
  title: 'No pudimos confirmar el cierre de tu inspección',
  message: 'Vuelve a intentarlo. Si el problema sigue, contacta a soporte.',
  retry: 'Reintentar',
} as const;

/**
 * Lo que ve el asegurado con el cierre ya confirmado mientras no llega a la pantalla siguiente,
 * que sin red puede tardar o no cargar. «Continuar» solo vuelve a intentar la navegación: el cierre
 * no se repite.
 */
export const CLOSE_CONFIRMED_TEXT = {
  title: 'Tu inspección quedó enviada',
  message: 'Si no pasas a la siguiente pantalla, revisa tu conexión y toca «Continuar».',
  continue: 'Continuar',
} as const;
